/**
 * engineNormal.ts - Normal template engine
 * Pure ES Module version
 */

import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { IEngine } from '../../base/engine/iEngine.js';
import type { IEngineTimings } from '../../base/engine/iEngineTimings.js';
import { EngineTimingData } from '../../model/engineTimingData.js';
import type { MergeResult } from '../../model/mergeResult.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import type { ILoaderNormal } from '../loader/iLoaderNormal.js';

// Import from split files
import { mergeMethods } from './engineNormal_Merge.js';
import { mergeSlotMethods } from './engineNormal_Merge_Slot.js';

/**
 * IndexOf-based template engine implementation for improved performance
 * Similar to EngineNormalJson but uses direct HTML string manipulation instead of HtmlJson processing
 * Returns (Html, OperationsMetadata?) tuple like EngineNormalJson
 */
class EngineNormal extends IEngine implements IEngineTimings {

    // #region Private Variable

    // Loader provided at construction time - ensures type safety
    _loader: ILoaderNormal | null = null;
    // Flag to gate detailed timing (set per mergeTemplates call)
    _genOperationJson: boolean = false;

    // Timing instrumentation (instance-scoped) - all times in nanoseconds
    _getTemplateNanos: number = 0;
    _jsonMergeNanos: number = 0;
    _slotProcessNanos: number = 0;
    _placeholderProcessNanos: number = 0;
    _dynamicDataNanos: number = 0;
    _dynamicViewNanos: number = 0;
    _dynamicActionNanos: number = 0;
    _dynamicClientDataNanos: number = 0;
    _getTemplateCount: number = 0;
    _jsonMergeCount: number = 0;
    _slotProcessCount: number = 0;
    _placeholderProcessCount: number = 0;
    _dynamicDataCount: number = 0;
    _dynamicViewCount: number = 0;
    _dynamicActionCount: number = 0;
    _dynamicClientDataCount: number = 0;

    // #endregion

    // #region Metrics Related

    resetTimings(): void {
        this._getTemplateNanos = 0;
        this._jsonMergeNanos = 0;
        this._slotProcessNanos = 0;
        this._placeholderProcessNanos = 0;
        this._dynamicDataNanos = 0;
        this._dynamicViewNanos = 0;
        this._dynamicActionNanos = 0;
        this._dynamicClientDataNanos = 0;
        this._getTemplateCount = 0;
        this._jsonMergeCount = 0;
        this._slotProcessCount = 0;
        this._placeholderProcessCount = 0;
        this._dynamicDataCount = 0;
        this._dynamicViewCount = 0;
        this._dynamicActionCount = 0;
        this._dynamicClientDataCount = 0;
    }

    getTimingReport(): string {
        return `[Normal Timing] GetTemplate: ${(this._getTemplateNanos / 1_000_000).toFixed(2)}ms (${this._getTemplateCount} calls), ` +
            `JsonMerge: ${(this._jsonMergeNanos / 1_000_000).toFixed(2)}ms (${this._jsonMergeCount} calls), ` +
            `SlotProcess: ${(this._slotProcessNanos / 1_000_000).toFixed(2)}ms (${this._slotProcessCount} calls), ` +
            `PlaceholderProcess: ${(this._placeholderProcessNanos / 1_000_000).toFixed(2)}ms (${this._placeholderProcessCount} calls), ` +
            `DynamicData: ${(this._dynamicDataNanos / 1_000_000).toFixed(2)}ms (${this._dynamicDataCount} calls), ` +
            `DynamicView: ${(this._dynamicViewNanos / 1_000_000).toFixed(2)}ms (${this._dynamicViewCount} calls), ` +
            `DynamicAction: ${(this._dynamicActionNanos / 1_000_000).toFixed(2)}ms (${this._dynamicActionCount} calls), ` +
            `DynamicClientData: ${(this._dynamicClientDataNanos / 1_000_000).toFixed(2)}ms (${this._dynamicClientDataCount} calls)`;
    }

    getTimingData(): EngineTimingData {
        const timingData = new EngineTimingData();
        timingData.getTemplateNanos = this._getTemplateNanos;
        timingData.getTemplateCount = this._getTemplateCount;
        timingData.jsonMergeNanos = this._jsonMergeNanos;
        timingData.jsonMergeCount = this._jsonMergeCount;
        timingData.slotProcessNanos = this._slotProcessNanos;
        timingData.slotProcessCount = this._slotProcessCount;
        timingData.placeholderProcessNanos = this._placeholderProcessNanos;
        timingData.placeholderProcessCount = this._placeholderProcessCount;
        timingData.dynamicDataNanos = this._dynamicDataNanos;
        timingData.dynamicDataCount = this._dynamicDataCount;
        timingData.dynamicViewNanos = this._dynamicViewNanos;
        timingData.dynamicViewCount = this._dynamicViewCount;
        timingData.dynamicActionNanos = this._dynamicActionNanos;
        timingData.dynamicActionCount = this._dynamicActionCount;
        timingData.dynamicClientDataNanos = this._dynamicClientDataNanos;
        timingData.dynamicClientDataCount = this._dynamicClientDataCount;
        return timingData;
    }

    accumulateDynamicDataTiming(nanos: number, count: number): void {
        this._dynamicDataNanos += nanos;
        this._dynamicDataCount += count;
    }

    accumulateDynamicViewTiming(nanos: number, count: number): void {
        this._dynamicViewNanos += nanos;
        this._dynamicViewCount += count;
    }

    accumulateDynamicActionTiming(nanos: number, count: number): void {
        this._dynamicActionNanos += nanos;
        this._dynamicActionCount += count;
    }

    accumulateDynamicClientDataTiming(nanos: number, count: number): void {
        this._dynamicClientDataNanos += nanos;
        this._dynamicClientDataCount += count;
    }

    ticksToNanos(nanos: number): number {
        return nanos; // Node already uses nanoseconds from process.hrtime
    }

    addSlotProcessTiming(nanos: number): void {
        this._slotProcessNanos += nanos;
        this._slotProcessCount++;
    }

    addPlaceholderProcessTiming(nanos: number): void {
        this._placeholderProcessNanos += nanos;
        this._placeholderProcessCount++;
    }

    addJsonMergeTiming(nanos: number): void {
        this._jsonMergeNanos += nanos;
        this._jsonMergeCount++;
    }

    addGetTemplateTiming(nanos: number): void {
        this._getTemplateNanos += nanos;
        this._getTemplateCount++;
    }

    createProtectedMergeContext(appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject): JsonObject {
        // Build a FRESH immutable merge context. The identity keys are written first onto the new
        // (always mutable) object, so the write can never be silently dropped by a read-only source —
        // that silent drop is the leak that let a stale/blank EngineType survive in some paths/langs.
        // The caller's keys are then copied in only where they don't collide, so the caller can never
        // override AppSite/AppView/AppFile/EngineType. The passed contextJson is never mutated (its
        // values are cloned, nothing is stamped onto it, it is never locked), so the same instance flows
        // unchanged into every engine and serializes plainly into the api-json (no leaked EngineType to
        // diverge across engines or config sources).
        const mergeContextJson = new JsonObject();
        mergeContextJson.set('AppSite', JsonValue.string(appSite));
        mergeContextJson.set('AppView', JsonValue.string(appView || ''));
        mergeContextJson.set('AppFile', JsonValue.string(appFile));
        mergeContextJson.set('EngineType', JsonValue.string(engineType));
        for (const [key, value] of contextJson.entries()) {
            if (!mergeContextJson.containsKey(key)) {
                mergeContextJson.set(key, value.deepClone());
            }
        }
        mergeContextJson.isReadOnly = true;
        return mergeContextJson;
    }

    createProtectedMergeConfig(configJson: JsonObject | null): JsonObject | null {
        if (configJson) {
            configJson.isReadOnly = true;
        }
        return configJson;
    }

    // #endregion

    // #region Constructor

    /**
     * Creates a new EngineNormal instance with the specified loader.
     * The loader is stored and used for all subsequent operations, ensuring type safety.
     */
    constructor(loader: ILoaderNormal) {
        super();
        if (!loader) {
            throw new Error('loader is required');
        }
        this._loader = loader;
    }

    // #endregion

    // #region IEngine - Merge To Output (Public)

    mergeToBytes(appSite: string, appView: string | null, contextJson: JsonObject | null): Uint8Array {
        const mergedHtml = this.mergeToString(appSite, appView, contextJson);
        return new TextEncoder().encode(mergedHtml);
    }

    mergeToString(appSite: string, appView: string | null, contextJson: JsonObject | null): string {
        const result = this.mergeTemplates(appSite, appView, false, false, contextJson ?? new JsonObject());
        return result.componentHtml.get(result.appFile).asString() || '';
    }

    // #endregion

    // Method implemented via Object.assign from engineNormal_Merge.ts
    declare mergeTemplates: (appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, configJson?: JsonObject | null, actionMode?: boolean, clientMode?: boolean) => MergeResult;
    declare mergeTemplateSlots: (contentHtml: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJsonBound: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplateBound: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, ops: import('../../model/templateOperation.js').TemplateOperation[], ctxJson: JsonObject | null, cfgJson: JsonObject | null) => string, replaceTemplatePlaceholders: (engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplate: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: import('../../model/templateOperation.js').TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null) => string, configJson?: JsonObject | null) => string, mergeConfigJson: JsonObject | null) => string;
    declare processTemplateSlots: (contentHtml: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJsonBound: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplateBound: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, ops: import('../../model/templateOperation.js').TemplateOperation[], ctxJson: JsonObject | null, cfgJson: JsonObject | null) => string, replaceTemplatePlaceholders: (engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplate: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: import('../../model/templateOperation.js').TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null) => string, configJson?: JsonObject | null) => string, mergeConfigJson: JsonObject | null) => string;
    declare extractSlotContents: (innerContent: string, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJsonBound: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplateBound: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, ops: import('../../model/templateOperation.js').TemplateOperation[], ctxJson: JsonObject | null, cfgJson: JsonObject | null) => string, replaceTemplatePlaceholders: (engine: EngineNormal, html: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], depth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null, flowStack: string[], getTemplateWithJson: (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) => { html: string | null; json: JsonObject | null; resolvedTemplateName: string | null; resolvedAppSite: string | null }, mergeJsonIntoTemplate: (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, operations: import('../../model/templateOperation.js').TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null) => string, configJson?: JsonObject | null) => string, mergeConfigJson: JsonObject | null) => Record<string, { processedContent: string; operations: import('../../model/templateOperation.js').TemplateOperation[] }>;
    declare prepareProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null) => { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null };
    declare initializeDynamicMergeState: (appSite: string, appFile: string, appView: string | null, actionMode: boolean) => DynamicResults;
    declare executeMergeIterativeExpansion: (processedHtml: string, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: import('../../model/templateOperation.js').TemplateOperation[], componentHtml: JsonObject, genComponentHtml: boolean, mergeContextJson: JsonObject | null, flowStack: string[], boundCallbacks: import('./engineNormal_Helper.js').MergeBoundCallbacks, mergeConfigJson: JsonObject | null) => string;
    declare unescapeProtectedDataValues: (componentHtml: JsonObject) => void;
    declare collectDynamicHandlerResults: (dynamicResults: DynamicResults) => { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject };

    // #endregion
}

Object.assign(EngineNormal.prototype, {
    ...mergeSlotMethods,
    ...mergeMethods
});

// ES6 export
export { EngineNormal };
